import type { CSSProperties } from 'react'
import type { TextPanel } from './cipher-data'

import { forwardRef, useState } from 'react'

import { cn } from '@/lib/utils'

interface CipherPanelProps {
    data: TextPanel
}

function ctaStyle(hover: boolean, pressed: boolean): CSSProperties {
    if (pressed) {
        return {
            background: 'var(--cipher-fg)',
            color: 'var(--cipher-bg)',
            boxShadow: '1px 1px 0 var(--cipher-shadow)',
            transform: 'translate(5px, 5px)'
        }
    }

    if (hover) {
        return {
            background: 'var(--cipher-bg)',
            color: 'var(--cipher-fg)',
            boxShadow: '14px 14px 0 var(--cipher-shadow)',
            transform: 'translate(-5px, -5px)'
        }
    }

    return {
        background: 'var(--cipher-fg)',
        color: 'var(--cipher-bg)',
        boxShadow: '5px 5px 0 var(--cipher-shadow)',
        transform: 'translate(0, 0)'
    }
}

// Each phase renders one of these. Position, opacity, slide and blur are all
// driven imperatively by the parent through the forwarded ref, so this
// component only re-renders for its own CTA hover/press state.
export const CipherPanel = forwardRef<HTMLDivElement, CipherPanelProps>(({ data }, ref) => {
    const [hover, setHover] = useState(false)
    const [pressed, setPressed] = useState(false)

    return (
        <div
            ref={ref}
            className='absolute box-border flex flex-col justify-start gap-7 px-2 text-center md:justify-between md:gap-0 md:px-0 md:text-left'
            style={{ opacity: 0 }}
        >
            <div className='flex flex-col gap-3 md:gap-4'>
                <span className='cipher-eyebrow'>
                    {data.eyebrow}
                </span>

                <h2 className='cipher-title' style={{ fontFamily: 'var(--font-pixelify-sans), system-ui, sans-serif' }}>
                    {data.title}
                </h2>

                <p className='cipher-body hidden md:block'>
                    {data.body}
                </p>
            </div>

            <div className='flex flex-col gap-5'>
                <span className='cipher-verified hidden md:block'>
                    {data.verified}
                </span>

                <ul className='m-0 flex list-none flex-col gap-4 p-0'>
                    {data.reviews.map((review, i) => (
                        <li
                            key={i}
                            className={cn('md:border-l-2 md:pl-[0.9rem]', i === 1 && 'hidden md:block')}
                            style={{ borderColor: 'currentColor' }}
                        >
                            <p className='cipher-quote'>
                                {review.quote}
                            </p>

                            <p className='cipher-attr mt-2'>
                                <span style={{ fontWeight: 700 }}>
                                    {review.author}
                                </span>{' '}
                                <span style={{ opacity: 0.65 }}>
                                    {review.role}
                                </span>
                            </p>
                        </li>
                    ))}
                </ul>

                <div className='flex justify-center md:justify-start'>
                    <a
                        href='#cipher'
                        className='cipher-cta'
                        style={ctaStyle(hover, pressed)}
                        onClick={e => e.preventDefault()}
                        onMouseEnter={() => setHover(true)}
                        onPointerEnter={() => setHover(true)}
                        onMouseLeave={() => {
                            setHover(false)
                            setPressed(false)
                        }}
                        onPointerLeave={() => {
                            setHover(false)
                            setPressed(false)
                        }}
                        onMouseDown={() => setPressed(true)}
                        onPointerDown={() => setPressed(true)}
                        onMouseUp={() => setPressed(false)}
                        onPointerUp={() => setPressed(false)}
                    >
                        {data.cta}

                        <span
                            aria-hidden='true'
                            style={{
                                display: 'inline-block',
                                marginLeft: '0.6rem',
                                transform: hover ? 'translateX(4px)' : 'translateX(0)',
                                transition: 'transform 180ms ease'
                            }}
                        >
                            →
                        </span>
                    </a>
                </div>

                <span className='cipher-meta hidden md:block'>
                    {data.meta}
                </span>
            </div>
        </div>
    )
})

CipherPanel.displayName = 'CipherPanel'
